'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { Document, DocumentUpload, GalleryRemove, Trash } from 'iconsax-react';

import { cn } from '@shared/lib';

import { language, useSingleUploader } from '../model';
import { TSingleUploaderProps } from '../model/types';

export const SingleUploader = ({
  value,
  onChange,
  uploadFn,
  accept = 'image/*',
  disabled = false,
  maxSize = 5,
  dimensions,
  dimensionErrorMessage,
  onDimensionsValidationChange,
  title = language.defaultTitle,
  hint,
  className,
  onError,
}: TSingleUploaderProps) => {
  const { state, refs, handlers } = useSingleUploader({
    value,
    onChange,
    uploadFn,
    accept,
    maxSize,
    dimensions,
    dimensionErrorMessage,
    onDimensionsValidationChange,
    onError,
  });

  const { status, percent, errorMessage, dragOver, previewUrl, isImage, fileName, hasValue } = state;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <input
        ref={refs.inputRef}
        type='file'
        className='hidden'
        accept={accept}
        disabled={disabled}
        onChange={handlers.handleInputChange}
        data-testid='single-uploader-input'
      />

      {!hasValue && status !== 'error' ? (
        <motion.div
          initial={{ opacity: 0.8 }}
          animate={{ opacity: 1 }}
          onDragOver={handlers.handleDragOver}
          onDragLeave={handlers.handleDragLeave}
          onDrop={disabled ? undefined : handlers.handleDrop}
          onClick={disabled ? undefined : handlers.openFileDialog}
          className={cn(
            'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-center transition-colors',
            dragOver ? 'border-blue-1 bg-blue-50' : 'border-disableText hover:bg-hover',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          )}
        >
          <DocumentUpload size={24} className='text-blue-1' />
          <div className='text-blue-1 body-14-medium'>{title}</div>
          {hint && <div className='text-icons body-10-regular'>{hint}</div>}
        </motion.div>
      ) : (
        <div className='flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-3'>
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            {status === 'error' ? (
              <GalleryRemove size={40} className='shrink-0 text-red-1' />
            ) : isImage && previewUrl ? (
              <img
                src={previewUrl}
                alt={fileName || language.previewAlt}
                className='h-12 w-12 shrink-0 rounded-md object-cover'
              />
            ) : (
              <Document size={32} className='shrink-0 text-gray-500' />
            )}

            <div className='flex min-w-0 flex-1 flex-col gap-1'>
              {status === 'error' ? (
                <span className='body-14-medium text-red-1'>{errorMessage ?? language.uploadError}</span>
              ) : (
                <span className='truncate body-14-medium text-text' title={fileName}>
                  {fileName || language.uploadedFile}
                </span>
              )}

              {status === 'uploading' && (
                <div className='flex items-center gap-2'>
                  <div className='relative h-1 flex-1 overflow-hidden rounded-xl bg-gray-200'>
                    <motion.div
                      className='absolute right-0 top-0 h-full bg-purple'
                      initial={{ width: '0%' }}
                      animate={{ width: `${percent}%` }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    />
                  </div>
                  <span className='body-12-regular text-purple'>{percent}%</span>
                </div>
              )}
            </div>
          </div>

          <div className='flex shrink-0 items-center gap-1'>
            {status === 'error' && !disabled && (
              <button
                type='button'
                onClick={handlers.openFileDialog}
                className='rounded px-2 py-1 text-blue-1 hover:bg-hover body-12-medium'
              >
                {language.retry}
              </button>
            )}
            {!disabled && status !== 'uploading' && (
              <button
                type='button'
                onClick={handlers.handleRemove}
                className='rounded p-1 text-red-1 hover:bg-hover'
                title={language.remove}
              >
                <Trash size={18} />
              </button>
            )}
          </div>
        </div>
      )}

      <AnimatePresence>
        {errorMessage && status !== 'error' && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className='body-12-regular text-red-1'
          >
            {errorMessage}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SingleUploader;
